import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { categoryKeys, competencyKeys, skillKeys } from "@/lib/query";
import type { CategoryCreateInput, CategoryUpdateInput } from "@/schemas/categories";

export interface Category {
  id: string;
  name: string;
  code: string;
  description: string | null;
  /** Palette token (`chart-1` …) or null. Resolve with `categoryColorVar`. */
  color: string | null;
  sortOrder: number;
  skillCount: number;
  competencyCount: number;
  /** Review questions pointing at it — also a reason a delete gets refused. */
  reviewCriterionCount: number;
  createdAt: string;
  updatedAt: string;
}

export function useCategories() {
  return useQuery({
    queryKey: categoryKeys.all,
    queryFn: async (): Promise<Category[]> => {
      const res = await fetch("/api/categories");
      if (!res.ok) throw new Error("Failed to fetch categories");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CategoryCreateInput) => {
      const res = await fetch("/api/categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Create failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      toast.success("Category created");
    },
    onError: () => toast.error("Failed to create category"),
  });
}

export function useUpdateCategory(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CategoryUpdateInput) => {
      const res = await fetch(`/api/categories/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Update failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      // Skill and competency views render this category's name — refresh them
      // so a rename doesn't leave stale labels behind.
      queryClient.invalidateQueries({ queryKey: skillKeys.all });
      queryClient.invalidateQueries({ queryKey: competencyKeys.all });
      toast.success("Category updated");
    },
    onError: () => toast.error("Failed to update category"),
  });
}

export function useReorderCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: "up" | "down" }) => {
      const res = await fetch(`/api/categories/${id}/reorder`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ direction }),
      });
      if (!res.ok) throw new Error("Reorder failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
    },
    onError: () => toast.error("Failed to reorder category"),
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/categories/${id}`, { method: "DELETE" });
      if (!res.ok) {
        // The refusal carries the reason — rows still using it, a built-in
        // value — and saying "Delete failed" instead left the admin with no
        // idea what to fix, while the next tab along explained itself.
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? "Delete failed");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      // A deleted category clears its label from any skill/competency showing it.
      queryClient.invalidateQueries({ queryKey: skillKeys.all });
      queryClient.invalidateQueries({ queryKey: competencyKeys.all });
      toast.success("Category deleted");
    },
    onError: () => toast.error("Failed to delete category"),
  });
}

/** A skill or competency as shown in a category's membership panel. */
export interface CategoryMember {
  id: string;
  name: string;
  description: string | null;
  status: "draft" | "active" | "archived";
}

export function useCategorySkills(categoryId: string | null) {
  return useQuery({
    queryKey: categoryKeys.skillMembers(categoryId ?? ""),
    enabled: categoryId !== null,
    queryFn: async (): Promise<CategoryMember[]> => {
      const res = await fetch(`/api/categories/${categoryId}/skills`);
      if (!res.ok) throw new Error("Failed to fetch the category's skills");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCategoryCompetencies(categoryId: string | null) {
  return useQuery({
    queryKey: categoryKeys.competencyMembers(categoryId ?? ""),
    enabled: categoryId !== null,
    queryFn: async (): Promise<CategoryMember[]> => {
      const res = await fetch(`/api/categories/${categoryId}/competencies`);
      if (!res.ok) throw new Error("Failed to fetch the category's competencies");
      const json = await res.json();
      return json.data;
    },
  });
}

/**
 * Add to, or remove from, one category's skill or competency membership.
 *
 * Both halves share a hook because they invalidate the same three things and
 * differ only in the verb — and because the panel calls them from one click
 * each, which is the whole point of the screen.
 */
function useCategoryMembership(
  categoryId: string,
  kind: "skills" | "competencies",
  method: "POST" | "DELETE",
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]): Promise<{ changed: number }> => {
      const res = await fetch(`/api/categories/${categoryId}/${kind}`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error("Failed to update the category");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      // The membership panel, the count on the category row, and the
      // Categories column on the skill/competency list all move together.
      if (kind === "skills") {
        queryClient.invalidateQueries({ queryKey: categoryKeys.skillMembers(categoryId) });
        queryClient.invalidateQueries({ queryKey: skillKeys.all });
      } else {
        queryClient.invalidateQueries({ queryKey: categoryKeys.competencyMembers(categoryId) });
        queryClient.invalidateQueries({ queryKey: competencyKeys.all });
      }
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
    },
    onError: (e: Error) => toast.error(e.message ?? "Failed to update the category"),
  });
}

export function useAddSkillsToCategory(categoryId: string) {
  return useCategoryMembership(categoryId, "skills", "POST");
}

export function useRemoveSkillsFromCategory(categoryId: string) {
  return useCategoryMembership(categoryId, "skills", "DELETE");
}

export function useAddCompetenciesToCategory(categoryId: string) {
  return useCategoryMembership(categoryId, "competencies", "POST");
}

export function useRemoveCompetenciesFromCategory(categoryId: string) {
  return useCategoryMembership(categoryId, "competencies", "DELETE");
}
